/**
 * Mailboxes, aliases, tenant settings, legal hold and SCIM provisioning
 * (ADR-0014). Every action here calls one platform route and reloads; the
 * API decides what is allowed, so a refusal is shown as the API words it.
 */

import { createContext, useContext, useState } from 'react';
import type {
  AdminAwayRules,
  AliasRecord,
  ColdTierStatus,
  DirectorySync,
  IdentityProviderMode,
  IdentityProviderState,
  MailboxDetail,
  MailboxList,
  MailboxRecord,
  PlatformNodeJob,
  ScimToken,
  ScimTokenIssued,
  SendAsMember,
  SignInStatus,
  TemporaryPassword,
  TenantSettings,
} from 'zitimail-shared-types';

import { api, describeError } from './api';
import { href } from './router';
import { ActionButton, Badge, CopyButton, Loaded, useLoad, when } from './ui';

const GIB = 1024 ** 3;
const MIB = 1024 ** 2;

const platformBase = (slug: string) => `/platform/tenants/${encodeURIComponent(slug)}`;

/**
 * Where these screens talk to and link to. The platform console uses the
 * default; webmail's tenant administration provides the tenant's own routes
 * (`/tenants/{slug}/admin`), which the API serves from the same handlers.
 */
export interface AdminWhere {
  readonly base: (slug: string) => string;
  /** eDiscovery is not under the admin routes: it has its own role (ADR-0021). */
  readonly ediscovery: (slug: string) => string;
  readonly mailboxHref: (slug: string, email: string) => string;
  readonly tenantHref: (slug: string) => string;
}

export const AdminWhereContext = createContext<AdminWhere>({
  base: platformBase,
  ediscovery: (slug) => `${platformBase(slug)}/ediscovery`,
  mailboxHref: (slug, email) => href({ page: 'mailbox', slug, email }),
  tenantHref: (slug) => href({ page: 'tenant', slug }),
});

/** Webmail's side: the tenant's own routes, and links within webmail's hash. */
export const TENANT_WHERE: AdminWhere = {
  base: (slug) => `/tenants/${encodeURIComponent(slug)}/admin`,
  ediscovery: (slug) => `/tenants/${encodeURIComponent(slug)}/ediscovery`,
  mailboxHref: (_slug, email) => `#/admin/mailboxes/${encodeURIComponent(email)}`,
  tenantHref: () => '#/admin/mailboxes',
};

/** Within this share of a limit, the usage is shown as nearly full. */
const NEARLY_FULL = 0.9;

/**
 * Seats and storage against the tenant's limits, where both the operator and
 * the tenant's own administrators see them first: a full tenant cannot add a
 * mailbox, and the reason should be on screen before anyone tries.
 */
export function Usage({
  seats,
  storage,
}: {
  seats: { readonly used: number; readonly limit: number };
  storage: { readonly used: number; readonly allocated: number; readonly limit: number };
}) {
  const level = (used: number, limit: number) =>
    limit <= 0 ? '' : used >= limit ? 'full' : used >= limit * NEARLY_FULL ? 'near' : '';
  const seatLevel = level(seats.used, seats.limit);
  const storageLevel = level(storage.allocated, storage.limit);
  return (
    <div className="adm-usage" aria-label="Seats and storage">
      <div className={`adm-usage-item ${seatLevel}`}>
        <span className="adm-usage-label">Seats</span>
        <span className="adm-usage-value">
          {seats.limit > 0 ? `${seats.used} of ${seats.limit} used` : `${seats.used} used`}
        </span>
        <span className="adm-usage-note">
          {seats.limit <= 0
            ? 'No limit set'
            : seatLevel === 'full'
              ? 'All seats are used: a new mailbox needs a seat freed or the limit raised'
              : `${seats.limit - seats.used} free`}
        </span>
        {seats.limit > 0 && (
          <meter min={0} max={seats.limit} value={Math.min(seats.used, seats.limit)} />
        )}
      </div>
      <div className={`adm-usage-item ${storageLevel}`}>
        <span className="adm-usage-label">Storage</span>
        <span className="adm-usage-value">
          {storage.limit > 0
            ? `${size(storage.allocated)} of ${size(storage.limit)} given out`
            : `${size(storage.allocated)} given out`}
        </span>
        <span className="adm-usage-note">
          {size(storage.used)} in use by mail
          {storage.limit <= 0
            ? ' · no limit set'
            : storageLevel === 'full'
              ? ' · full: a new mailbox needs space freed or the limit raised'
              : ''}
        </span>
        {storage.limit > 0 && (
          <meter min={0} max={storage.limit} value={Math.min(storage.allocated, storage.limit)} />
        )}
      </div>
    </div>
  );
}

export function size(bytes: number): string {
  if (bytes >= GIB) {
    return `${(bytes / GIB).toFixed(bytes % GIB === 0 ? 0 : 1)} GiB`;
  }
  if (bytes >= MIB) {
    return `${Math.round(bytes / MIB)} MiB`;
  }
  return `${Math.round(bytes / 1024)} KiB`;
}

/** "198 KiB of 5 GiB", or the quota alone before the mail store has measured it. */
function usage(m: MailboxRecord): string {
  return m.quota_used_at
    ? `${size(m.quota_used_bytes)} of ${size(m.quota_bytes)}`
    : size(m.quota_bytes);
}

function StateBadge({ mailbox, suspended }: { mailbox: MailboxRecord; suspended?: boolean }) {
  return (
    <span className="adm-badges">
      <Badge
        tone={mailbox.state === 'active' ? 'ok' : mailbox.state === 'disabled' ? 'warn' : 'crit'}
      >
        {mailbox.state}
      </Badge>
      {mailbox.legal_hold && <Badge tone="warn">legal hold</Badge>}
      {suspended && <Badge tone="crit">sending suspended</Badge>}
      {mailbox.provisioned_by === 'scim' && <Badge tone="neutral">SCIM</Badge>}
    </span>
  );
}

// ─── The tenant's mailboxes ──────────────────────────────────────────────────

export function MailboxesPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base, mailboxHref } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<MailboxList>(`${base(slug)}/mailboxes`, { signal }),
    `mailboxes:${slug}`,
  );
  const [filter, setFilter] = useState('');
  const [show, setShow] = useState<'all' | 'active' | 'disabled' | 'deleted'>('all');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [quota, setQuota] = useState('');
  const [withSignIn, setWithSignIn] = useState(true);
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <section className="adm-panel">
      <Loaded load={load}>
        {({ mailboxes, seats, storage, retention_days }) => {
          const q = filter.trim().toLowerCase();
          const shown = mailboxes.filter(
            (m) =>
              (show === 'all' || m.state === show) &&
              (!q ||
                m.email.includes(q) ||
                (m.display_name ?? '').toLowerCase().includes(q) ||
                (m.external_id ?? '').toLowerCase().includes(q)),
          );
          const count = (state: string) => mailboxes.filter((m) => m.state === state).length;
          return (
            <>
              <div className="adm-panel-head">
                <h3>Mailboxes</h3>
                <span className="adm-muted">
                  Deleted mailboxes are restorable for {retention_days} days
                </span>
              </div>
              <Usage seats={seats} storage={storage} />
              <div className="adm-inline adm-filter">
                <input
                  type="search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Find by address, name or IdP id"
                  aria-label="Find a mailbox"
                />
                <div className="adm-seg" role="radiogroup" aria-label="Show">
                  {(['all', 'active', 'disabled', 'deleted'] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      role="radio"
                      aria-checked={show === s}
                      className={show === s ? 'on' : ''}
                      onClick={() => setShow(s)}
                    >
                      {s === 'all' ? `All ${mailboxes.length}` : `${s} ${count(s)}`}
                    </button>
                  ))}
                </div>
              </div>
              {shown.length === 0 ? (
                <p className="adm-muted">
                  {mailboxes.length === 0 ? 'No mailboxes yet.' : 'Nothing matches.'}
                </p>
              ) : (
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Address</th>
                      <th>Name</th>
                      <th>State</th>
                      <th>Storage</th>
                      <th>App passwords</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((m) => (
                      <tr key={m.id}>
                        <td className="adm-mono">
                          <a href={mailboxHref(slug, m.email)}>{m.email}</a>
                        </td>
                        <td>{m.display_name ?? '—'}</td>
                        <td>
                          <StateBadge mailbox={m} />
                          {m.state === 'deleted' && (
                            <span className="adm-small adm-muted">
                              {' '}
                              purges {when(m.purge_after)}
                            </span>
                          )}
                        </td>
                        <td>{usage(m)}</td>
                        <td>{m.app_passwords}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          );
        }}
      </Loaded>
      {active && (
        <form
          className="adm-inline"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            setIssued(null);
            const created = email;
            api(`${base(slug)}/mailboxes`, {
              method: 'POST',
              body: {
                email,
                ...(displayName ? { display_name: displayName } : {}),
                ...(quota ? { quota_mb: Math.round(Number(quota) * 1024) } : {}),
              },
            })
              .then(() =>
                withSignIn
                  ? api<TemporaryPassword>(
                      `${base(slug)}/mailboxes/${encodeURIComponent(created)}/sign-in`,
                      { method: 'POST' },
                    ).then(
                      (made) => setIssued({ email: created, password: made.temporary_password }),
                      (e: unknown) =>
                        setError(
                          `Mailbox created, but not its sign-in: ${describeError(e)}. Create it from the mailbox's page.`,
                        ),
                    )
                  : undefined,
              )
              .then(() => {
                setEmail('');
                setDisplayName('');
                setQuota('');
                reload();
              })
              .catch((e: unknown) => setError(describeError(e)));
          }}
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value.toLowerCase())}
            placeholder="someone@domain"
            aria-label="Mailbox address"
            required
          />
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Display name"
            aria-label="Display name"
          />
          <input
            type="number"
            min={0.1}
            step={0.1}
            value={quota}
            onChange={(e) => setQuota(e.target.value)}
            placeholder="Quota GiB (default)"
            aria-label="Quota in GiB"
            className="adm-narrow"
          />
          <label className="adm-check">
            <input
              type="checkbox"
              checked={withSignIn}
              onChange={(e) => setWithSignIn(e.target.checked)}
            />{' '}
            Create a sign-in
          </label>
          <button type="submit" className="btn ghost">
            Create mailbox
          </button>
          {error && <span className="adm-error">{error}</span>}
        </form>
      )}
      {issued && (
        <TemporaryPasswordReveal
          email={issued.email}
          password={issued.password}
          onDone={() => setIssued(null)}
        />
      )}
      <p className="adm-muted adm-small">
        With a sign-in, the person gets a temporary password to change at first sign-in. A tenant
        whose users come from its directory signs in with the directory account instead. With SCIM
        on, the tenant&apos;s identity provider creates and removes mailboxes itself.
      </p>
      {active && <BulkCreate slug={slug} onDone={reload} />}
    </section>
  );
}

// ─── Many mailboxes from a CSV file ──────────────────────────────────────────

export interface BulkRow {
  readonly email: string;
  readonly displayName: string;
  /** GiB; empty for the tenant's default. */
  readonly quota: string;
}

/** The most rows one import takes: a spreadsheet per department, not a migration. */
export const BULK_MAX = 500;

/** Splits CSV text into rows of cells, honouring quotes ("a, b" and "" for a quote). */
export function csvCells(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (c === '"') {
        quoted = false;
      } else {
        cell += c;
      }
    } else if (c === '"') {
      quoted = true;
    } else if (c === ',' || c === ';' || c === '\t') {
      row.push(cell);
      cell = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += c;
    }
  }
  row.push(cell);
  rows.push(row);
  return rows.map((r) => r.map((c) => c.trim())).filter((r) => r.some((c) => c !== ''));
}

/**
 * The people a CSV names: columns email, display name, quota in GiB (the last
 * two optional). A header row is recognised by an "email" cell and skipped.
 */
export function parseBulk(text: string): { rows: BulkRow[]; problems: string[] } {
  const cells = csvCells(text);
  const first = cells[0];
  const header = first?.some((c) => /^e-?mail( address)?$/i.test(c)) ?? false;
  const col = (re: RegExp, fallback: number) => {
    const at = header && first ? first.findIndex((c) => re.test(c)) : -1;
    return at >= 0 ? at : fallback;
  };
  const emailAt = col(/^e-?mail( address)?$/i, 0);
  const nameAt = col(/name/i, 1);
  const quotaAt = col(/quota|gib|gb/i, 2);
  const rows: BulkRow[] = [];
  const problems: string[] = [];
  const seen = new Set<string>();
  (header ? cells.slice(1) : cells).forEach((r, i) => {
    const line = i + (header ? 2 : 1);
    const email = (r[emailAt] ?? '').toLowerCase();
    const quota = r[quotaAt] ?? '';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      problems.push(`Line ${line}: "${email}" is not an email address`);
    } else if (seen.has(email)) {
      problems.push(`Line ${line}: ${email} appears twice`);
    } else if (quota !== '' && !(Number(quota) > 0)) {
      problems.push(`Line ${line}: quota "${quota}" is not a number of GiB`);
    } else {
      seen.add(email);
      rows.push({ email, displayName: r[nameAt] ?? '', quota });
    }
  });
  if (rows.length > BULK_MAX) {
    problems.push(`${rows.length} people: import at most ${BULK_MAX} at a time`);
  }
  return { rows, problems };
}

interface BulkResult {
  readonly email: string;
  readonly outcome: string;
  readonly password: string;
}

/** The results as CSV, for the administrator to hand out the passwords. */
function resultsCsv(results: readonly BulkResult[]): string {
  const q = (v: string) => `"${v.replace(/"/g, '""')}"`;
  return ['email,result,temporary password']
    .concat(results.map((r) => [r.email, r.outcome, r.password].map(q).join(',')))
    .join('\r\n');
}

export function BulkCreate({ slug, onDone }: { slug: string; onDone: () => void }) {
  const { base } = useContext(AdminWhereContext);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [withSignIn, setWithSignIn] = useState(true);
  const [results, setResults] = useState<BulkResult[] | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const parsed = parseBulk(text);
  if (!open) {
    return (
      <p>
        <button type="button" className="btn ghost" onClick={() => setOpen(true)}>
          Create many from a CSV file…
        </button>
      </p>
    );
  }
  const run = async () => {
    const done: BulkResult[] = [];
    for (const [i, row] of parsed.rows.entries()) {
      setProgress(`Creating ${i + 1} of ${parsed.rows.length}: ${row.email}`);
      try {
        await api(`${base(slug)}/mailboxes`, {
          method: 'POST',
          body: {
            email: row.email,
            ...(row.displayName ? { display_name: row.displayName } : {}),
            ...(row.quota ? { quota_mb: Math.round(Number(row.quota) * 1024) } : {}),
          },
        });
      } catch (e) {
        done.push({ email: row.email, outcome: `not created: ${describeError(e)}`, password: '' });
        continue;
      }
      if (!withSignIn) {
        done.push({ email: row.email, outcome: 'created', password: '' });
        continue;
      }
      try {
        const made = await api<TemporaryPassword>(
          `${base(slug)}/mailboxes/${encodeURIComponent(row.email)}/sign-in`,
          { method: 'POST' },
        );
        done.push({
          email: row.email,
          outcome: 'created with sign-in',
          password: made.temporary_password,
        });
      } catch (e) {
        done.push({
          email: row.email,
          outcome: `created, sign-in not: ${describeError(e)}`,
          password: '',
        });
      }
    }
    setProgress(null);
    setResults(done);
    onDone();
  };
  const blocked = parsed.rows.length === 0 || parsed.problems.length > 0;
  return (
    <div className="adm-panel">
      <h3>Create many mailboxes</h3>
      <p className="adm-muted">
        One person per line: <span className="adm-mono">email, display name, quota in GiB</span>.
        Name and quota are optional; a header row is fine. Save a spreadsheet as CSV, or paste the
        columns here.
      </p>
      <div className="adm-inline">
        <input
          type="file"
          accept=".csv,text/csv,text/plain"
          aria-label="CSV file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void file.text().then(setText);
          }}
        />
        <label className="adm-check">
          <input
            type="checkbox"
            checked={withSignIn}
            onChange={(e) => setWithSignIn(e.target.checked)}
          />{' '}
          Create their sign-ins
        </label>
      </div>
      <textarea
        className="adm-textarea"
        rows={6}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setResults(null);
        }}
        placeholder={'email,name,quota\njane@example.com,Jane Tan,10'}
        aria-label="CSV text"
      />
      {parsed.problems.length > 0 && (
        <ul className="adm-error" role="alert">
          {parsed.problems.slice(0, 10).map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      <div className="adm-inline">
        <ActionButton run={run} disabled={blocked || progress !== null}>
          {parsed.rows.length === 1 ? 'Create 1 mailbox' : `Create ${parsed.rows.length} mailboxes`}
        </ActionButton>
        <button type="button" className="adm-copy" onClick={() => setOpen(false)}>
          Close
        </button>
        {progress && <span className="adm-muted">{progress}</span>}
      </div>
      {results && (
        <div className="adm-reveal" role="status">
          <strong>
            {results.filter((r) => r.outcome.startsWith('created')).length} of {results.length}{' '}
            created. Download the results now: the temporary passwords are not shown again.
          </strong>
          <a
            className="btn"
            download={`mailboxes-${slug}.csv`}
            href={`data:text/csv;charset=utf-8,${encodeURIComponent(resultsCsv(results))}`}
          >
            Download results
          </a>
          <table className="adm-table">
            <thead>
              <tr>
                <th>Mailbox</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.email}>
                  <td className="adm-mono">{r.email}</td>
                  <td>{r.outcome}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── One mailbox ─────────────────────────────────────────────────────────────

export function MailboxPage({ slug, email }: { slug: string; email: string }) {
  const { base, tenantHref } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) =>
      api<MailboxDetail>(`${base(slug)}/mailboxes/${encodeURIComponent(email)}`, { signal }),
    `mailbox:${slug}:${email}`,
  );
  const path = `${base(slug)}/mailboxes/${encodeURIComponent(email)}`;
  const post = (suffix: string, body?: unknown) =>
    api(`${path}${suffix}`, { method: 'POST', ...(body !== undefined ? { body } : {}) }).then(
      reload,
    );
  return (
    <>
      <div className="adm-head">
        <a href={tenantHref(slug)} className="adm-back">
          ← {slug}
        </a>
      </div>
      <Loaded load={load}>
        {({ mailbox: m, aliases, sending }) => (
          <>
            <div className="adm-head">
              <h2 className="adm-mono">{m.email}</h2>
              <StateBadge mailbox={m} suspended={sending?.suspended === true} />
            </div>
            <dl className="adm-facts">
              <dt>Name</dt>
              <dd>{m.display_name ?? '—'}</dd>
              <dt>Storage</dt>
              <dd>
                {m.quota_used_at
                  ? `${usage(m)} used, measured ${when(m.quota_used_at)}`
                  : `${size(m.quota_bytes)} quota; not measured yet`}
              </dd>
              <dt>Managed by</dt>
              <dd>
                {m.provisioned_by === 'scim'
                  ? `the tenant's identity provider (SCIM${m.external_id ? `, id ${m.external_id}` : ''})`
                  : 'this console'}
              </dd>
              <dt>App passwords</dt>
              <dd>{m.app_passwords} working</dd>
              <dt>Created</dt>
              <dd>{when(m.created_at)}</dd>
              {m.disabled_at && (
                <>
                  <dt>Disabled</dt>
                  <dd>
                    {when(m.disabled_at)}
                    {m.disabled_by && ` by ${m.disabled_by}`}
                    {m.disabled_reason && ` — ${m.disabled_reason}`}
                  </dd>
                </>
              )}
              {m.deleted_at && (
                <>
                  <dt>Deleted</dt>
                  <dd>
                    {when(m.deleted_at)} · restorable until {when(m.purge_after)}
                  </dd>
                </>
              )}
              {m.legal_hold && (
                <>
                  <dt>Legal hold</dt>
                  <dd>{m.legal_hold_reason ?? 'yes'}</dd>
                </>
              )}
            </dl>
            {m.provisioned_by === 'scim' && (
              <p className="adm-note">
                The identity provider manages this mailbox. Changes made here are overwritten by its
                next sync; change the person in the identity provider instead.
              </p>
            )}

            {m.state !== 'deleted' && <EditMailbox mailbox={m} path={path} onSaved={reload} />}

            {m.state !== 'deleted' && m.provisioned_by !== 'scim' && (
              <ChangeAddress slug={slug} mailbox={m} path={path} />
            )}

            <section className="adm-panel">
              <h3>Access</h3>
              {sending?.suspended && (
                <div className="adm-inline">
                  <ActionButton
                    confirm={`Allow ${m.email} to send again? Reset its password, remove app passwords the person does not recognise and sign it out everywhere first, or whoever is sending the spam carries on.`}
                    run={() => post('/allow-sending')}
                  >
                    Allow sending again
                  </ActionButton>
                  <p className="adm-muted adm-small">
                    Sending suspended{sending.since ? ` ${when(sending.since)}` : ''}: it{' '}
                    {sending.reason ?? 'was stopped by the outbound guard'}. Mail to it still
                    arrives. This usually means someone else has its password or an app password.
                  </p>
                </div>
              )}
              {m.state === 'active' && (
                <Disable onDisable={(reason) => post('/disable', { reason })} />
              )}
              {m.state === 'disabled' && (
                <div className="adm-inline">
                  <ActionButton run={() => post('/enable')}>Enable mailbox</ActionButton>
                  <p className="adm-muted adm-small">Sign-in and delivery start again at once.</p>
                </div>
              )}
              {m.state !== 'deleted' && (
                <SignOut
                  onSignOut={(revoke) => post('/sign-out', { revoke_app_passwords: revoke })}
                />
              )}
              {m.state === 'deleted' && (
                <div className="adm-inline">
                  <ActionButton run={() => post('/restore')}>Restore mailbox</ActionButton>
                  <p className="adm-muted adm-small">
                    Brings it back with its mail{m.disabled_at ? ', still disabled' : ''}.
                  </p>
                </div>
              )}
            </section>

            {m.state !== 'deleted' && <SignIn path={path} email={m.email} />}

            {m.state !== 'deleted' && <AwayRules path={path} />}

            {m.state !== 'deleted' && <MailboxAccess path={path} email={m.email} />}

            {m.state !== 'deleted' && <SendAs path={path} email={m.email} />}

            {m.state !== 'deleted' && <MailboxRecovery path={path} />}

            <MailboxAliases slug={slug} mailbox={m} aliases={aliases} onChanged={reload} />

            <section className="adm-panel adm-danger">
              <h3>Retention</h3>
              <LegalHold
                held={m.legal_hold}
                reason={m.legal_hold_reason}
                what="this mailbox"
                onChange={(hold, reason) => post('/legal-hold', { hold, reason })}
              />
              {m.state !== 'deleted' ? (
                <DeleteMailbox
                  onDelete={(days) =>
                    post('/delete', days === null ? {} : { retention_days: days })
                  }
                />
              ) : (
                <PurgeMailbox
                  mailbox={m}
                  onPurge={(early) => post('/purge', { confirm_email: m.email, early })}
                />
              )}
            </section>
          </>
        )}
      </Loaded>
    </>
  );
}

const RECOVERY_TONE: Record<string, 'ok' | 'warn' | 'crit' | 'neutral'> = {
  queued: 'neutral',
  running: 'neutral',
  succeeded: 'ok',
  failed: 'crit',
  expired: 'warn',
};

/**
 * The recovery jobs (ADR-0032), run by the agent on the node holding the
 * mailbox. Neither deletes anything; both are audited with who asked.
 */
function MailboxRecovery({ path }: { path: string }) {
  const [days, setDays] = useState('30');
  const [load, reload] = useLoad(
    (signal) => api<{ jobs: PlatformNodeJob[] }>(`${path}/recoveries`, { signal }),
    `recoveries:${path}`,
  );
  const queue = async (suffix: string, body?: unknown) => {
    await api(`${path}${suffix}`, { method: 'POST', ...(body !== undefined ? { body } : {}) });
    reload();
  };
  return (
    <section className="adm-panel">
      <h3>Recovery</h3>
      <div className="adm-inline">
        <label>
          Deleted in the last
          <input
            type="number"
            min={1}
            max={30}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className="adm-narrow"
            aria-label="Days of deleted mail"
          />
          days
        </label>
        <ActionButton tone="ghost" run={() => queue('/recover-deleted', { days: Number(days) })}>
          Recover deleted mail
        </ActionButton>
        <p className="adm-muted adm-small">
          Puts mail deleted for good back in the folders it was deleted from. The person can also do
          this themselves in webmail.
        </p>
      </div>
      <div className="adm-inline">
        <ActionButton tone="ghost" run={() => queue('/restore-snapshot')}>
          Restore the latest snapshot
        </ActionButton>
        <p className="adm-muted adm-small">
          Copies the mailbox as it was at its latest snapshot, taken every hour, into a Recovered
          folder beside what it holds now.
        </p>
      </div>
      <Loaded load={load}>
        {({ jobs }) =>
          jobs.length === 0 ? (
            <p className="adm-muted adm-small">No recoveries run for this mailbox.</p>
          ) : (
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Asked</th>
                  <th>What</th>
                  <th>By</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id}>
                    <td>{when(j.requested_at)}</td>
                    <td>{j.kind === 'restore_snapshot' ? 'Snapshot restore' : 'Deleted mail'}</td>
                    <td>{j.requested_by}</td>
                    <td>
                      <Badge tone={RECOVERY_TONE[j.state] ?? 'neutral'}>{j.state}</Badge>
                      {j.state === 'failed' && j.output && (
                        <span className="adm-muted adm-small"> {j.output.slice(0, 200)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </Loaded>
    </section>
  );
}

function EditMailbox({
  mailbox,
  path,
  onSaved,
}: {
  mailbox: MailboxRecord;
  path: string;
  onSaved: () => void;
}) {
  const [name, setName] = useState(mailbox.display_name ?? '');
  const [quota, setQuota] = useState(String(+(mailbox.quota_bytes / GIB).toFixed(2)));
  return (
    <section className="adm-panel">
      <h3>Details</h3>
      <div className="adm-inline">
        <label>
          Display name
          <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Display name" />
        </label>
        <label>
          Quota (GiB)
          <input
            type="number"
            min={0.1}
            step={0.1}
            value={quota}
            onChange={(e) => setQuota(e.target.value)}
            className="adm-narrow"
            aria-label="Quota in GiB"
          />
        </label>
        <ActionButton
          tone="ghost"
          run={async () => {
            await api(path, {
              method: 'POST',
              body: { display_name: name, quota_mb: Math.round(Number(quota) * 1024) },
            });
            onSaved();
          }}
        >
          Save details
        </ActionButton>
      </div>
    </section>
  );
}

/**
 * A new address for the mailbox (ADR-0044): its mail, calendar and contacts
 * stay; the person signs in with the new address from now on.
 */
export function ChangeAddress({
  slug,
  mailbox,
  path,
}: {
  slug: string;
  mailbox: MailboxRecord;
  path: string;
}) {
  const { mailboxHref } = useContext(AdminWhereContext);
  const [address, setAddress] = useState('');
  const [keep, setKeep] = useState(true);
  return (
    <section className="adm-panel">
      <h3>Address</h3>
      <div className="adm-inline">
        <input
          type="email"
          value={address}
          onChange={(e) => setAddress(e.target.value.toLowerCase())}
          placeholder={mailbox.email}
          aria-label="New address"
        />
        <label className="adm-check">
          <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} /> Keep
          receiving mail sent to the old address
        </label>
        <ActionButton
          tone="ghost"
          disabled={!address.includes('@') || address === mailbox.email}
          run={async () => {
            const done = await api<{ email: string }>(`${path}/rename`, {
              method: 'POST',
              body: { new_email: address, keep_old_address: keep },
            });
            window.location.assign(mailboxHref(slug, done.email));
          }}
        >
          Change address
        </ActionButton>
      </div>
      <p className="adm-muted adm-small">
        Mail, calendar, contacts and app passwords stay. The person is signed out and signs in with
        the new address; groups that included the old address now include the new one.
      </p>
    </section>
  );
}

function Disable({ onDisable }: { onDisable: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  return (
    <div className="adm-inline">
      <input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (shown to administrators)"
        aria-label="Reason for disabling"
      />
      <ActionButton
        tone="danger"
        confirm="Disable this mailbox? The person is signed out at once and cannot sign in; their mail is kept and new mail is refused until you enable it again."
        run={() => onDisable(reason)}
      >
        Disable mailbox
      </ActionButton>
      <p className="adm-muted adm-small">
        Stops sign-in and refuses new mail at once, and ends open sessions. Mail is kept.
      </p>
    </div>
  );
}

/** A temporary password, shown once: the administrator hands it over. */
export function TemporaryPasswordReveal({
  email,
  password,
  onDone,
}: {
  email: string;
  password: string;
  onDone: () => void;
}) {
  return (
    <div className="adm-reveal" role="status">
      <strong>Temporary password for {email}. Copy it now; it is not shown again.</strong>
      <code className="adm-mono">{password}</code>
      <CopyButton text={password} label="Copy password" />
      <button type="button" className="adm-copy" onClick={onDone}>
        Done
      </button>
      <p className="adm-muted adm-small">
        Give it to the person by a channel other than this mailbox. They choose their own password
        at first sign-in.
      </p>
    </div>
  );
}

/**
 * The person's sign-in (ADR-0041): create it, reset the password or the
 * second factor, unlock it, and the administrator role. A tenant whose users
 * come from its directory manages passwords and accounts there; the second
 * factor and the lock after failed sign-ins are kept here, so those stay.
 */
export function SignIn({ path, email }: { path: string; email: string }) {
  const [load, reload] = useLoad(
    (signal) => api<SignInStatus>(`${path}/sign-in`, { signal }),
    `sign-in:${path}`,
  );
  const [issued, setIssued] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const run = async (suffix: string, done: string, body?: unknown) => {
    setNote(null);
    await api(`${path}${suffix}`, { method: 'POST', ...(body !== undefined ? { body } : {}) });
    setNote(done);
    reload();
  };
  const reveal = async (suffix: string) => {
    setNote(null);
    const made = await api<TemporaryPassword>(`${path}${suffix}`, { method: 'POST' });
    setIssued(made.temporary_password);
    reload();
  };
  return (
    <section className="adm-panel">
      <h3>Sign-in</h3>
      {issued && (
        <TemporaryPasswordReveal email={email} password={issued} onDone={() => setIssued(null)} />
      )}
      <Loaded load={load}>
        {(st) =>
          st.directory_managed ? (
            <>
              <p className="adm-muted">
                This organisation&apos;s sign-ins come from its directory (Active Directory or
                LDAP). Reset passwords and manage accounts there.
              </p>
              {st.exists && (
                <div className="adm-inline">
                  <ActionButton
                    confirm={
                      "Reset this person's second factor? They set up their authenticator again at their next sign-in."
                    }
                    tone="ghost"
                    run={() =>
                      run(
                        '/reset-second-factor',
                        'Authenticator apps and security keys removed; they set one up again at next sign-in.',
                      )
                    }
                  >
                    Reset second factor
                  </ActionButton>
                  <ActionButton
                    tone="ghost"
                    run={() => run('/unlock', 'Unlocked; they can try signing in again.')}
                  >
                    Unlock
                  </ActionButton>
                  <p className="adm-muted adm-small">
                    Second factor: for a lost or replaced phone. Unlock: after too many wrong
                    attempts here. Both are kept here, not in the directory; their password, and any
                    lock in the directory itself, stay with the directory.
                  </p>
                </div>
              )}
            </>
          ) : !st.exists ? (
            <div className="adm-inline">
              <ActionButton run={() => reveal('/sign-in')}>Create sign-in</ActionButton>
              <p className="adm-muted adm-small">
                This person cannot sign in yet. Creating a sign-in gives a temporary password they
                change at first sign-in.
              </p>
            </div>
          ) : (
            <>
              <p>
                Can sign in{st.admin ? ' and administers this organisation' : ''}.{' '}
                {st.admin && <Badge tone="neutral">administrator</Badge>}
              </p>
              <div className="adm-inline">
                <ActionButton
                  confirm={
                    "Reset this person's password? They are signed out everywhere, their app passwords stop working, and you get a temporary password to give them."
                  }
                  tone="ghost"
                  run={() => reveal('/reset-password')}
                >
                  Reset password
                </ActionButton>
                <ActionButton
                  confirm={
                    "Reset this person's second factor? They set up their authenticator again at their next sign-in."
                  }
                  tone="ghost"
                  run={() =>
                    run(
                      '/reset-second-factor',
                      'Authenticator apps and security keys removed; they set one up again at next sign-in.',
                    )
                  }
                >
                  Reset second factor
                </ActionButton>
                <ActionButton
                  tone="ghost"
                  run={() => run('/unlock', 'Unlocked; they can try signing in again.')}
                >
                  Unlock
                </ActionButton>
                <ActionButton
                  confirm={
                    st.admin
                      ? "Remove this person's administrator rights?"
                      : 'Make this person an administrator of the organisation? They can then manage every mailbox.'
                  }
                  tone="ghost"
                  run={() =>
                    run(
                      '/admin',
                      st.admin
                        ? 'No longer an administrator from their next sign-in.'
                        : 'Administrator from their next sign-in.',
                      { admin: !st.admin },
                    )
                  }
                >
                  {st.admin ? 'Remove administrator role' : 'Make administrator'}
                </ActionButton>
              </div>
              <p className="adm-muted adm-small">
                A password reset also signs them out everywhere. Second factor: for a lost or
                replaced phone. Unlock: after too many wrong passwords.
              </p>
            </>
          )
        }
      </Loaded>
      {note && (
        <p className="adm-muted" role="status">
          {note}
        </p>
      )}
    </section>
  );
}

/**
 * An automatic reply or forward the administrator sets for the person
 * (ADR-0042): it runs before their own rules and does not touch them.
 */
export function AwayRules({ path }: { path: string }) {
  const [load, reload] = useLoad(
    (signal) => api<{ rules: AdminAwayRules | null }>(`${path}/away`, { signal }),
    `away:${path}`,
  );
  return (
    <section className="adm-panel">
      <h3>Automatic reply and forwarding</h3>
      <Loaded load={load}>
        {({ rules }) => (
          <AwayForm key={rules?.updated_at ?? 'none'} path={path} rules={rules} onSaved={reload} />
        )}
      </Loaded>
    </section>
  );
}

function AwayForm({
  path,
  rules,
  onSaved,
}: {
  path: string;
  rules: AdminAwayRules | null;
  onSaved: () => void;
}) {
  const [replyOn, setReplyOn] = useState(Boolean(rules?.reply_body));
  const [subject, setSubject] = useState(rules?.reply_subject ?? '');
  const [body, setBody] = useState(rules?.reply_body ?? '');
  const [from, setFrom] = useState(rules?.reply_from ?? '');
  const [until, setUntil] = useState(rules?.reply_until ?? '');
  const [forwardOn, setForwardOn] = useState(Boolean(rules?.forward_to));
  const [to, setTo] = useState(rules?.forward_to ?? '');
  const [keep, setKeep] = useState(rules?.forward_keep ?? true);
  const save = (clear: boolean) =>
    api(`${path}/away`, {
      method: 'POST',
      body:
        clear || (!replyOn && !forwardOn)
          ? {}
          : {
              reply: replyOn
                ? {
                    body,
                    ...(subject ? { subject } : {}),
                    ...(from ? { from } : {}),
                    ...(until ? { until } : {}),
                  }
                : null,
              forward: forwardOn ? { to, keep } : null,
            },
    }).then(onSaved);
  return (
    <>
      {rules && (
        <p className="adm-muted">
          Set by {rules.updated_by}, {when(rules.updated_at)}.
        </p>
      )}
      <label className="adm-check">
        <input type="checkbox" checked={replyOn} onChange={(e) => setReplyOn(e.target.checked)} />{' '}
        Reply automatically
      </label>
      {replyOn && (
        <div>
          <div className="adm-inline">
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject (optional)"
              aria-label="Reply subject"
              maxLength={200}
            />
            <label>
              From
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                aria-label="Reply from"
              />
            </label>
            <label>
              Until
              <input
                type="date"
                value={until}
                onChange={(e) => setUntil(e.target.value)}
                aria-label="Reply until"
              />
            </label>
          </div>
          <textarea
            className="adm-textarea"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="I am away until Monday. For anything urgent, write to …"
            aria-label="Reply text"
            maxLength={10000}
          />
        </div>
      )}
      <label className="adm-check">
        <input
          type="checkbox"
          checked={forwardOn}
          onChange={(e) => setForwardOn(e.target.checked)}
        />{' '}
        Forward new mail
      </label>
      {forwardOn && (
        <div className="adm-inline">
          <input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value.toLowerCase())}
            placeholder="colleague@domain"
            aria-label="Forward to"
          />
          <label className="adm-check">
            <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} />{' '}
            Keep a copy in this mailbox
          </label>
        </div>
      )}
      <div className="adm-inline">
        <ActionButton
          run={() => save(false)}
          disabled={(replyOn && !body.trim()) || (forwardOn && !to.trim())}
        >
          Save
        </ActionButton>
        {rules && (
          <ActionButton tone="ghost" run={() => save(true)}>
            Turn both off
          </ActionButton>
        )}
      </div>
      <p className="adm-muted adm-small">
        For someone on leave, or who has left. Runs before the person&apos;s own rules and does not
        change them; spam is never answered or forwarded. A sender gets at most one reply a day.
        Forwarding outside the organisation follows its forwarding policy.
      </p>
    </>
  );
}

/**
 * Who may send from this mailbox's address (ADR-0045): a shared sales@, or
 * an assistant for a manager. The mail plane enforces it for every client.
 */
export function SendAs({ path, email }: { path: string; email: string }) {
  const [load, reload] = useLoad(
    (signal) => api<{ members: SendAsMember[] }>(`${path}/send-as`, { signal }),
    `send-as:${path}`,
  );
  const [member, setMember] = useState('');
  const change = async (who: string, allow: boolean) => {
    await api(`${path}/send-as`, { method: 'POST', body: { member: who, allow } });
    setMember('');
    reload();
  };
  return (
    <section className="adm-panel">
      <h3>Send as</h3>
      <Loaded load={load}>
        {({ members }) =>
          members.length === 0 ? (
            <p className="adm-muted">Only {email} sends from this address.</p>
          ) : (
            <table className="adm-table">
              <thead>
                <tr>
                  <th>May send as {email}</th>
                  <th>Granted</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {members.map((x) => (
                  <tr key={x.email}>
                    <td className="adm-mono">{x.email}</td>
                    <td>
                      {when(x.granted_at)} by {x.granted_by}
                    </td>
                    <td>
                      <ActionButton tone="ghost" run={() => change(x.email, false)}>
                        Remove
                      </ActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </Loaded>
      <div className="adm-inline">
        <input
          type="email"
          value={member}
          onChange={(e) => setMember(e.target.value.toLowerCase())}
          placeholder="colleague@domain"
          aria-label="Person who may send as this mailbox"
        />
        <ActionButton
          tone="ghost"
          disabled={!member.includes('@')}
          run={() => change(member, true)}
        >
          Allow to send as
        </ActionButton>
      </div>
      <p className="adm-muted adm-small">
        They choose this address in From when writing, in webmail or any mail app. Being in a group
        is not permission to send as it.
      </p>
    </section>
  );
}

const SYNC_WORDS: Record<string, string> = {
  queued: 'Waiting for the mail store to apply the last change…',
  running: 'The mail store is applying the last change…',
  succeeded: 'Applied',
  failed: 'The mail store could not apply the last change',
  expired: 'The mail store did not pick up the last change; save again',
};

/**
 * Who may open this mailbox's folders (ADR-0047): a team's shared sales@, or
 * a manager's for an assistant. They see it as Shared/<address> in webmail
 * and in their mail app.
 */
export function MailboxAccess({ path, email }: { path: string; email: string }) {
  const [load, reload] = useLoad(
    (signal) =>
      api<{ members: SendAsMember[]; sync: PlatformNodeJob | null }>(`${path}/access`, {
        signal,
      }),
    `access:${path}`,
  );
  const [member, setMember] = useState('');
  const change = async (who: string, allow: boolean) => {
    await api(`${path}/access`, { method: 'POST', body: { member: who, allow } });
    setMember('');
    reload();
    // The store's agent applies it within seconds; show when it has.
    window.setTimeout(reload, 4000);
  };
  return (
    <section className="adm-panel">
      <h3>Shared with</h3>
      <Loaded load={load}>
        {({ members, sync }) => (
          <>
            {members.length === 0 ? (
              <p className="adm-muted">Only {email} opens this mailbox.</p>
            ) : (
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Can open {email}</th>
                    <th>Granted</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {members.map((x) => (
                    <tr key={x.email}>
                      <td className="adm-mono">{x.email}</td>
                      <td>
                        {when(x.granted_at)} by {x.granted_by}
                      </td>
                      <td>
                        <ActionButton tone="ghost" run={() => change(x.email, false)}>
                          Remove
                        </ActionButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {sync && (
              <p className={sync.state === 'failed' ? 'adm-error' : 'adm-muted adm-small'}>
                {SYNC_WORDS[sync.state] ?? sync.state}
                {sync.finished_at ? `, ${when(sync.finished_at)}` : ''}.
                {sync.state === 'failed' && sync.output ? ` ${sync.output}` : ''}
              </p>
            )}
          </>
        )}
      </Loaded>
      <div className="adm-inline">
        <input
          type="email"
          value={member}
          onChange={(e) => setMember(e.target.value.toLowerCase())}
          placeholder="colleague@domain"
          aria-label="Person who may open this mailbox"
        />
        <ActionButton
          tone="ghost"
          disabled={!member.includes('@')}
          run={() => change(member, true)}
        >
          Share with
        </ActionButton>
      </div>
      <p className="adm-muted adm-small">
        They can read, file and delete its mail under Shared/{email}, in webmail and in Outlook,
        Apple Mail or Thunderbird. To send from its address too, allow it under Send as.
      </p>
    </section>
  );
}

function SignOut({ onSignOut }: { onSignOut: (revoke: boolean) => Promise<void> }) {
  const [revoke, setRevoke] = useState(true);
  return (
    <div className="adm-inline">
      <label className="adm-check">
        <input type="checkbox" checked={revoke} onChange={(e) => setRevoke(e.target.checked)} />{' '}
        Also revoke every app password
      </label>
      <ActionButton tone="ghost" run={() => onSignOut(revoke)}>
        Sign out everywhere
      </ActionButton>
      <p className="adm-muted adm-small">
        For a lost device or a suspected compromise. The mailbox stays active.
      </p>
    </div>
  );
}

export function LegalHold({
  held,
  reason,
  what,
  onChange,
}: {
  held: boolean;
  reason: string | null;
  what: string;
  onChange: (hold: boolean, reason?: string) => Promise<void>;
}) {
  const [text, setText] = useState('');
  return held ? (
    <div className="adm-inline">
      <span>
        <Badge tone="warn">legal hold</Badge> {reason}
      </span>
      <ActionButton tone="ghost" run={() => onChange(false)}>
        Lift legal hold
      </ActionButton>
    </div>
  ) : (
    <div className="adm-inline">
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Case or ticket reference"
        aria-label="Legal hold reason"
      />
      <ActionButton tone="ghost" disabled={!text.trim()} run={() => onChange(true, text.trim())}>
        Place legal hold
      </ActionButton>
      <p className="adm-muted adm-small">
        While held, {what} can still be deleted or suspended, but is never purged.
      </p>
    </div>
  );
}

function DeleteMailbox({ onDelete }: { onDelete: (days: number | null) => Promise<void> }) {
  const [days, setDays] = useState('');
  return (
    <div className="adm-inline">
      <label>
        Keep for
        <input
          type="number"
          min={1}
          max={3650}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          placeholder="tenant default"
          title="Leave empty for the tenant's setting"
          className="adm-narrow"
          aria-label="Retention days"
        />
        days
      </label>
      <ActionButton
        tone="danger"
        confirm="Delete this mailbox? The person is signed out at once and stops receiving mail. It can be restored until the retention ends."
        run={() => onDelete(days === '' ? null : Math.max(1, Number(days)))}
      >
        Delete mailbox
      </ActionButton>
      <p className="adm-muted adm-small">
        Stops it at once and holds the address. It can be restored, with its mail, until the
        retention ends; then it is purged.
      </p>
    </div>
  );
}

function PurgeMailbox({
  mailbox,
  onPurge,
}: {
  mailbox: MailboxRecord;
  onPurge: (early: boolean) => Promise<void>;
}) {
  const [confirm, setConfirm] = useState('');
  const due = mailbox.purge_after !== null && new Date(mailbox.purge_after).getTime() <= Date.now();
  return (
    <div className="adm-inline">
      <input
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder={`type ${mailbox.email} to confirm`}
        aria-label="Type the address to confirm the purge"
      />
      <ActionButton
        tone="danger"
        disabled={confirm.trim().toLowerCase() !== mailbox.email || mailbox.legal_hold}
        run={() => onPurge(!due)}
      >
        {due ? 'Purge permanently' : 'Purge now, before the retention ends'}
      </ActionButton>
      <p className="adm-muted adm-small">
        Deletes the mail, calendars, contacts and app passwords and frees the address. It cannot be
        undone.
        {mailbox.legal_hold && ' Refused while the legal hold applies.'}
      </p>
    </div>
  );
}

function MailboxAliases({
  slug,
  mailbox,
  aliases,
  onChanged,
}: {
  slug: string;
  mailbox: MailboxRecord;
  aliases: readonly AliasRecord[];
  onChanged: () => void;
}) {
  const { base } = useContext(AdminWhereContext);
  const [source, setSource] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <section className="adm-panel">
      <h3>Addresses that deliver here</h3>
      {aliases.length === 0 ? (
        <p className="adm-muted">Only {mailbox.email}.</p>
      ) : (
        <ul className="adm-list">
          {aliases.map((a) => (
            <li key={a.id}>
              <span className="adm-mono">{a.source}</span>
              {a.kind !== 'individual' && <Badge tone="neutral">{a.kind.replace('_', '-')}</Badge>}
              <ActionButton
                tone="ghost"
                confirm={`Remove ${a.source}? Mail sent to it is refused from now on.`}
                run={async () => {
                  await api(`${base(slug)}/aliases/${a.id}/delete`, { method: 'POST' });
                  onChanged();
                }}
              >
                Remove
              </ActionButton>
            </li>
          ))}
        </ul>
      )}
      {mailbox.state === 'active' && (
        <form
          className="adm-inline"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            api(`${base(slug)}/aliases`, {
              method: 'POST',
              body: { source, destination: mailbox.email },
            })
              .then(() => {
                setSource('');
                onChanged();
              })
              .catch((e: unknown) => setError(describeError(e)));
          }}
        >
          <input
            type="email"
            value={source}
            onChange={(e) => setSource(e.target.value.toLowerCase())}
            placeholder={`another-name@${mailbox.email.split('@')[1] ?? ''}`}
            aria-label="Alias address"
            required
          />
          <button type="submit" className="btn ghost">
            Add alias
          </button>
          {error && <span className="adm-error">{error}</span>}
        </form>
      )}
    </section>
  );
}

// ─── Aliases and groups, tenant-wide ─────────────────────────────────────────

export function AliasesPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<{ aliases: AliasRecord[] }>(`${base(slug)}/aliases`, { signal }),
    `aliases:${slug}`,
  );
  const [source, setSource] = useState('');
  const [destination, setDestination] = useState('');
  const [kind, setKind] = useState<'individual' | 'group' | 'catch_all'>('individual');
  const [error, setError] = useState<string | null>(null);
  return (
    <section className="adm-panel">
      <h3>Aliases, groups and catch-all</h3>
      <Loaded load={load}>
        {({ aliases }) =>
          aliases.length === 0 ? (
            <p className="adm-muted">None.</p>
          ) : (
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Delivers to</th>
                  <th>Kind</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {aliases.map((a) => (
                  <tr key={a.id}>
                    <td className="adm-mono">{a.source}</td>
                    <td className="adm-mono">{a.destination}</td>
                    <td>{a.kind === 'catch_all' ? 'catch-all' : a.kind}</td>
                    <td>
                      <ActionButton
                        tone="ghost"
                        confirm={`Remove ${a.source}? Mail sent to it is refused from now on.`}
                        run={async () => {
                          await api(`${base(slug)}/aliases/${a.id}/delete`, { method: 'POST' });
                          reload();
                        }}
                      >
                        Remove
                      </ActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </Loaded>
      {active && (
        <form
          className="adm-inline"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            api(`${base(slug)}/aliases`, { method: 'POST', body: { source, destination, kind } })
              .then(() => {
                setSource('');
                setDestination('');
                reload();
              })
              .catch((e: unknown) => setError(describeError(e)));
          }}
        >
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as typeof kind)}
            aria-label="Kind"
          >
            <option value="individual">Alias</option>
            <option value="group">Group member</option>
            <option value="catch_all">Catch-all</option>
          </select>
          <input
            value={source}
            onChange={(e) => setSource(e.target.value.toLowerCase())}
            placeholder={kind === 'catch_all' ? '@domain' : 'team@domain'}
            aria-label="Address"
            required
          />
          <span aria-hidden="true">→</span>
          <input
            type="email"
            value={destination}
            onChange={(e) => setDestination(e.target.value.toLowerCase())}
            placeholder="person@domain"
            aria-label="Delivers to"
            required
          />
          <button type="submit" className="btn ghost">
            Add
          </button>
          {error && <span className="adm-error">{error}</span>}
        </form>
      )}
      <p className="adm-muted adm-small">
        A group is one address with several members: add it once per member. A catch-all takes mail
        for addresses on the domain that match nothing else. Members whose mailbox is disabled are
        skipped.
      </p>
    </section>
  );
}

// ─── Settings and legal hold ─────────────────────────────────────────────────

export function TenantSettingsPanel({ slug, editable }: { slug: string; editable: boolean }) {
  const [load, reload] = useLoad(
    (signal) => api<TenantSettings>(`${platformBase(slug)}/settings`, { signal }),
    `settings:${slug}`,
  );
  return (
    <section className="adm-panel" id="plan-and-limits">
      <h3>Plan and limits</h3>
      <Loaded load={load}>
        {(settings) => (
          <SettingsForm
            key={JSON.stringify(settings)}
            slug={slug}
            settings={settings}
            editable={editable}
            onSaved={reload}
          />
        )}
      </Loaded>
    </section>
  );
}

function SettingsForm({
  slug,
  settings,
  editable,
  onSaved,
}: {
  slug: string;
  settings: TenantSettings;
  editable: boolean;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: settings.name,
    plan: settings.plan,
    max_users: settings.max_users,
    max_storage_gb: Math.round(settings.max_storage_bytes / GIB),
    mailbox_retention_days: settings.mailbox_retention_days,
    default_quota_gb: +(settings.default_quota_bytes / GIB).toFixed(2),
    // Empty is never.
    cold_after_days: settings.cold_after_days === null ? '' : String(settings.cold_after_days),
    archive_enabled: settings.archive_enabled,
    archive_years: +(settings.archive_retention_days / 365).toFixed(2),
    // Empty is never.
    attachment_link_mb:
      settings.attachment_link_mb === null ? '' : String(settings.attachment_link_mb),
    attachment_link_days: settings.attachment_link_days,
  });
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm({ ...form, [key]: value });
  return (
    <>
      <div className="adm-form-grid">
        <label>
          Name
          <input
            value={form.name}
            disabled={!editable}
            onChange={(e) => set('name', e.target.value)}
          />
        </label>
        <label>
          Plan
          <select
            value={form.plan}
            disabled={!editable}
            onChange={(e) => set('plan', e.target.value as TenantSettings['plan'])}
          >
            <option value="starter">Starter</option>
            <option value="business">Business</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </label>
        <label>
          Seats <span className="adm-muted">(0 = unlimited)</span>
          <input
            type="number"
            min={0}
            value={form.max_users}
            disabled={!editable}
            onChange={(e) => set('max_users', Number(e.target.value))}
          />
        </label>
        <label>
          Storage, GiB <span className="adm-muted">(0 = unlimited)</span>
          <input
            type="number"
            min={0}
            value={form.max_storage_gb}
            disabled={!editable}
            onChange={(e) => set('max_storage_gb', Number(e.target.value))}
          />
        </label>
        <label>
          New mailbox quota, GiB
          <input
            type="number"
            min={0.1}
            step={0.1}
            value={form.default_quota_gb}
            disabled={!editable}
            onChange={(e) => set('default_quota_gb', Number(e.target.value))}
          />
        </label>
        <label>
          Keep deleted mailboxes, days
          <input
            type="number"
            min={0}
            max={3650}
            value={form.mailbox_retention_days}
            disabled={!editable}
            onChange={(e) => set('mailbox_retention_days', Number(e.target.value))}
          />
        </label>
        <label>
          Move mail to cold storage after, days <span className="adm-muted">(empty = never)</span>
          <input
            type="number"
            min={1}
            max={3650}
            placeholder="Never"
            value={form.cold_after_days}
            disabled={!editable}
            onChange={(e) => set('cold_after_days', e.target.value)}
          />
        </label>
      </div>
      <ColdTierLine slug={slug} enabled={settings.cold_after_days !== null} />
      <div className="adm-form-grid">
        <label>
          Send attachments over, MB <span className="adm-muted">(empty = never)</span>
          <input
            type="number"
            min={1}
            max={2048}
            placeholder="Never"
            value={form.attachment_link_mb}
            disabled={!editable}
            onChange={(e) => set('attachment_link_mb', e.target.value)}
          />
        </label>
        <label>
          Links work for, days
          <input
            type="number"
            min={1}
            max={365}
            value={form.attachment_link_days}
            disabled={!editable}
            onChange={(e) => set('attachment_link_days', Number(e.target.value))}
          />
        </label>
      </div>
      <p className="adm-muted">
        Larger files go as a download link instead of an attachment. Recipients can download them
        until the link expires.
      </p>
      <div className="adm-sub">
        <h4>Compliance archive</h4>
        <p className="adm-muted">
          A copy of every message to or from this tenant, kept unchangeable and undeletable — by
          anyone, Genworx included — until the retention period ends. It cannot be shortened later
          for copies already kept.
        </p>
        <div className="adm-form-grid">
          <label className="adm-check">
            <input
              type="checkbox"
              checked={form.archive_enabled}
              disabled={!editable}
              onChange={(e) => set('archive_enabled', e.target.checked)}
            />
            Archive all mail
          </label>
          <label>
            Keep each copy for, years
            <input
              type="number"
              min={0.01}
              max={100}
              step={0.01}
              value={form.archive_years}
              disabled={!editable || !form.archive_enabled}
              onChange={(e) => set('archive_years', Number(e.target.value))}
            />
          </label>
        </div>
        {settings.archived_count > 0 && (
          <p className="adm-muted">
            {settings.archived_count.toLocaleString()} message
            {settings.archived_count === 1 ? '' : 's'} archived
            {settings.archived_at ? `, the latest ${when(settings.archived_at)}` : ''}.
          </p>
        )}
      </div>
      {editable && (
        <ActionButton
          tone="ghost"
          run={async () => {
            await api(`${platformBase(slug)}/settings`, {
              method: 'POST',
              body: {
                name: form.name,
                plan: form.plan,
                max_users: form.max_users,
                max_storage_gb: form.max_storage_gb,
                mailbox_retention_days: form.mailbox_retention_days,
                default_quota_mb: Math.round(form.default_quota_gb * 1024),
                cold_after_days:
                  form.cold_after_days.trim() === '' ? null : Number(form.cold_after_days),
                archive_enabled: form.archive_enabled,
                archive_retention_days: Math.max(1, Math.round(form.archive_years * 365)),
                attachment_link_mb:
                  form.attachment_link_mb.trim() === '' ? null : Number(form.attachment_link_mb),
                attachment_link_days: form.attachment_link_days,
              },
            });
            onSaved();
          }}
        >
          Save plan and limits
        </ActionButton>
      )}
      <div className="adm-sub">
        <h4>Legal hold</h4>
        <LegalHold
          held={settings.legal_hold}
          reason={settings.legal_hold_reason}
          what="every mailbox of this tenant, and the tenant itself,"
          onChange={(hold, reason) =>
            api(`${platformBase(slug)}/legal-hold`, {
              method: 'POST',
              body: { hold, reason },
            }).then(onSaved)
          }
        />
      </div>
    </>
  );
}

/**
 * Cold storage (ADR-0017): old mail on the mail server's cold disk (or a
 * MinIO bucket) instead of its data disk. People see one mailbox; their quota counts all of it. This
 * says when the mail server last moved this tenant's mail, and what happened.
 */
function ColdTierLine({ slug, enabled }: { slug: string; enabled: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load] = useLoad(
    (signal) => api<ColdTierStatus>(`${base(slug)}/cold-tier`, { signal }),
    `cold-tier:${slug}`,
  );
  return (
    <Loaded load={load}>
      {({ tiered_at, report }) =>
        // A pass from before the policy was cleared says nothing about now.
        !enabled || !tiered_at || !report ? (
          <p className="adm-muted">
            Old mail moves to cold storage once a day. It stays in people&apos;s mailboxes, opens as
            usual, and still counts towards their quota.
          </p>
        ) : report.error ? (
          <p className="adm-error" role="alert">
            Cold storage, {when(tiered_at)}: {report.error}
          </p>
        ) : (
          <p className="adm-muted">
            Cold storage: {when(tiered_at)}, mail older than {report.older_than_days} days in{' '}
            {report.mailboxes} mailbox{report.mailboxes === 1 ? '' : 'es'} moved by {report.node}.
          </p>
        )
      }
    </Loaded>
  );
}

// ─── SCIM ────────────────────────────────────────────────────────────────────

export function ScimPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<{ tokens: ScimToken[] }>(`${base(slug)}/scim-tokens`, { signal }),
    `scim:${slug}`,
  );
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState<ScimTokenIssued | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <section className="adm-panel">
      <h3>Provisioning from the tenant&apos;s identity provider (SCIM)</h3>
      <p className="adm-muted">
        Microsoft Entra ID, Okta and other SCIM 2.0 providers create mailboxes for joiners, update
        names, and disable mailboxes for leavers, ending their sessions and app passwords at once.
        In the provider, set the tenant URL to{' '}
        <span className="adm-mono">https://&lt;mail host&gt;/scim/v2/{slug}</span> and the secret
        token to one issued here. Map users only; groups are not provisioned.
      </p>
      {issued && (
        <div className="adm-reveal" role="status">
          <strong>Copy this token now. It is not shown again.</strong>
          <code className="adm-mono">{issued.token}</code>
          <CopyButton text={issued.token} label="Copy token" />
          <button type="button" className="adm-copy" onClick={() => setIssued(null)}>
            Done
          </button>
        </div>
      )}
      <Loaded load={load}>
        {({ tokens }) =>
          tokens.length === 0 ? (
            <p className="adm-muted">No tokens. SCIM is off for this tenant.</p>
          ) : (
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Token</th>
                  <th>Issued</th>
                  <th>Last used</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.id}>
                    <td>{t.label}</td>
                    <td className="adm-mono">{t.prefix}…</td>
                    <td>
                      {when(t.created_at)}
                      {t.created_by && (
                        <span className="adm-muted adm-small"> by {t.created_by}</span>
                      )}
                    </td>
                    <td>{when(t.last_used_at)}</td>
                    <td>
                      {t.revoked_at ? (
                        <Badge tone="neutral">revoked</Badge>
                      ) : (
                        <ActionButton
                          tone="ghost"
                          run={async () => {
                            await api(`${base(slug)}/scim-tokens/${t.id}/revoke`, {
                              method: 'POST',
                            });
                            reload();
                          }}
                        >
                          Revoke
                        </ActionButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </Loaded>
      {active && (
        <form
          className="adm-inline"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            api<ScimTokenIssued>(`${base(slug)}/scim-tokens`, { method: 'POST', body: { label } })
              .then((made) => {
                setIssued(made);
                setLabel('');
                reload();
              })
              .catch((e: unknown) => setError(describeError(e)));
          }}
        >
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Entra ID production"
            aria-label="Token name"
            required
          />
          <button type="submit" className="btn ghost">
            Issue token
          </button>
          {error && <span className="adm-error">{error}</span>}
        </form>
      )}
    </section>
  );
}

// ─── The organisation's own identity provider ────────────────────────────────

const IDP_MODES: Array<[IdentityProviderMode, string, string]> = [
  ['testing', 'Testing', 'Hidden: only Test sign-in reaches it.'],
  ['offered', 'Offered', 'A button on the sign-in page, beside the password.'],
  ['everyone', 'Everyone', 'Webmail, the apps and mail programs go straight to it.'],
];

/**
 * Sign-in through the organisation's own Microsoft Entra ID, Okta, Google
 * Workspace or other OpenID Connect provider (ADR-0059). `me`, on the
 * organisation's own admin page, is the administrator's address: their test
 * sign-in is what allows Everyone.
 */
export function IdentityProviderPanel({
  slug,
  active,
  me,
}: {
  slug: string;
  active: boolean;
  me?: string;
}) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<IdentityProviderState>(`${base(slug)}/identity-provider`, { signal }),
    `identity-provider:${slug}`,
  );
  return (
    <section className="adm-panel">
      <h3>Sign-in with your own identity provider</h3>
      <p className="adm-muted">
        People sign in with the account they already have at Microsoft Entra ID, Okta, Google
        Workspace or another OpenID Connect provider, and its own second step, instead of a password
        kept here. Their mailbox is matched by email address.
      </p>
      <Loaded load={load}>
        {(state) => (
          <IdentityProviderBody
            slug={slug}
            active={active}
            me={me}
            state={state}
            onChange={reload}
          />
        )}
      </Loaded>
    </section>
  );
}

function IdentityProviderBody({
  slug,
  active,
  me,
  state,
  onChange,
}: {
  slug: string;
  active: boolean;
  me: string | undefined;
  state: IdentityProviderState;
  onChange: () => void;
}) {
  const { base } = useContext(AdminWhereContext);
  const [editing, setEditing] = useState(false);
  const provider = state.provider;
  if (state.blocked === 'directory') {
    return (
      <p className="adm-muted" role="note">
        This organisation&apos;s people come from its directory (Active Directory or LDAP), which
        decides how they sign in.
      </p>
    );
  }
  if (state.blocked === 'foreign') {
    return (
      <p className="adm-muted" role="note">
        Sign-in already uses an identity provider set up by Genworx by hand (
        {state.foreign_providers.join(', ')}). Ask Genworx to remove it before connecting one here.
      </p>
    );
  }
  const redirect = (
    <p className="adm-inline">
      <span>Redirect URI to register at your provider:</span>
      <span className="adm-mono">{state.redirect_uri}</span>
      <CopyButton text={state.redirect_uri} />
    </p>
  );
  if (!provider || editing) {
    return (
      <>
        {redirect}
        {active && (
          <IdentityProviderForm
            slug={slug}
            current={provider}
            onSaved={() => {
              setEditing(false);
              onChange();
            }}
            onCancel={provider ? () => setEditing(false) : undefined}
          />
        )}
      </>
    );
  }
  const tested = state.linked_admins.length > 0;
  const people = state.linked_people_capped
    ? `${state.linked_people}+ people have`
    : state.linked_people === 1
      ? '1 person has'
      : `${state.linked_people} people have`;
  return (
    <>
      <dl className="adm-facts">
        <dt>Provider</dt>
        <dd>{provider.display_name}</dd>
        <dt>Discovery URL</dt>
        <dd className="adm-mono">{provider.discovery_url}</dd>
        <dt>Client ID</dt>
        <dd className="adm-mono">{provider.client_id}</dd>
        <dt>Administrators</dt>
        <dd>
          {provider.admin_claim
            ? `whoever carries ${provider.admin_claim} = ${provider.admin_value ?? ''}`
            : 'made here, as before'}
        </dd>
        <dt>New people</dt>
        <dd>
          {provider.create_mailboxes
            ? `a mailbox is made at their first sign-in through it, with the default quota and a seat${
                provider.mailbox_claim
                  ? `, for whoever carries ${provider.mailbox_claim} = ${provider.mailbox_value ?? ''}`
                  : ''
              }`
            : 'mailboxes are made here or by SCIM, as before'}
        </dd>
        <dt>Signed in through it</dt>
        <dd>
          {people} signed in through it
          {tested ? `, administrators included: ${state.linked_admins.join(', ')}` : ''}.
        </dd>
      </dl>
      {redirect}
      <div className="adm-inline" role="group" aria-label="Who signs in through it">
        {IDP_MODES.map(([mode, label, help]) => (
          <span key={mode} className="adm-idp-mode">
            {provider.mode === mode ? (
              <Badge tone="ok">{label}</Badge>
            ) : (
              active && (
                <ActionButton
                  tone="ghost"
                  disabled={mode === 'everyone' && !tested}
                  confirm={
                    mode === 'everyone'
                      ? `Send everyone straight to ${provider.display_name}? They no longer see the password page in webmail and the apps.`
                      : undefined
                  }
                  run={async () => {
                    await api(`${base(slug)}/identity-provider/mode`, {
                      method: 'POST',
                      body: { mode },
                    });
                    onChange();
                  }}
                >
                  {label}
                </ActionButton>
              )
            )}
            <span className="adm-muted adm-small">{help}</span>
          </span>
        ))}
      </div>
      {!tested && (
        <p className="adm-muted adm-small" role="note">
          Everyone becomes possible once an administrator of the organisation has signed in through
          the provider with Test sign-in, so a wrong setting cannot lock everyone out.
        </p>
      )}
      {active && (
        <div className="adm-inline">
          {me ? (
            <a
              className="btn ghost"
              href={`/auth/login?email=${encodeURIComponent(me)}&via=idp`}
              target="_blank"
              rel="noopener"
            >
              Test sign-in
            </a>
          ) : (
            <span className="adm-muted adm-small">
              An administrator of the organisation tests it from their own admin page in webmail.
            </span>
          )}
          <button type="button" className="btn ghost" onClick={() => setEditing(true)}>
            Change settings
          </button>
          <ActionButton
            tone="danger"
            confirm={`Disconnect ${provider.display_name}? ${people} signed in through it; anyone who never set a password here cannot sign in until an administrator resets one.`}
            run={async () => {
              await api(`${base(slug)}/identity-provider/disconnect`, { method: 'POST' });
              onChange();
            }}
          >
            Disconnect
          </ActionButton>
        </div>
      )}
    </>
  );
}

function IdentityProviderForm({
  slug,
  current,
  onSaved,
  onCancel,
}: {
  slug: string;
  current: IdentityProviderState['provider'];
  onSaved: () => void;
  onCancel: (() => void) | undefined;
}) {
  const { base } = useContext(AdminWhereContext);
  const [name, setName] = useState(current?.display_name ?? '');
  const [discovery, setDiscovery] = useState(current?.discovery_url ?? '');
  const [clientId, setClientId] = useState(current?.client_id ?? '');
  const [secret, setSecret] = useState('');
  const [claim, setClaim] = useState(current?.admin_claim ?? '');
  const [value, setValue] = useState(current?.admin_value ?? '');
  const [createMailboxes, setCreateMailboxes] = useState(current?.create_mailboxes ?? false);
  const [mailboxClaim, setMailboxClaim] = useState(current?.mailbox_claim ?? '');
  const [mailboxValue, setMailboxValue] = useState(current?.mailbox_value ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="adm-form"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        setBusy(true);
        api(`${base(slug)}/identity-provider`, {
          method: 'POST',
          body: {
            display_name: name,
            discovery_url: discovery,
            client_id: clientId,
            client_secret: secret,
            admin_claim: claim,
            admin_value: value,
            create_mailboxes: createMailboxes,
            mailbox_claim: createMailboxes ? mailboxClaim : '',
            mailbox_value: createMailboxes ? mailboxValue : '',
          },
        })
          .then(onSaved)
          .catch((e: unknown) => setError(describeError(e)))
          .finally(() => setBusy(false));
      }}
    >
      <div className="adm-fields">
        <label>
          Name on the sign-in button
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Microsoft"
            maxLength={60}
            required
          />
        </label>
        <label>
          Discovery URL
          <input
            type="url"
            value={discovery}
            onChange={(e) => setDiscovery(e.target.value)}
            placeholder="https://…/.well-known/openid-configuration"
            maxLength={500}
            required
            readOnly={!!current}
          />
        </label>
        <label>
          Client ID
          <input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            required
            readOnly={!!current}
          />
        </label>
        <label>
          Client secret
          <input
            type="password"
            autoComplete="off"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder={current ? 'unchanged' : ''}
            required={!current}
          />
        </label>
        <label>
          Administrators&apos; claim (optional)
          <input value={claim} onChange={(e) => setClaim(e.target.value)} placeholder="groups" />
        </label>
        <label>
          Claim value for administrators
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. the Entra group's object ID"
          />
        </label>
      </div>
      <label className="adm-check">
        <input
          type="checkbox"
          checked={createMailboxes}
          onChange={(e) => setCreateMailboxes(e.target.checked)}
        />{' '}
        Make a mailbox at a person&apos;s first sign-in through the provider
      </label>
      {createMailboxes && (
        <div className="adm-fields">
          <label>
            Only for people with the claim (optional)
            <input
              value={mailboxClaim}
              onChange={(e) => setMailboxClaim(e.target.value)}
              placeholder="groups"
            />
          </label>
          <label>
            Claim value for a mailbox
            <input
              value={mailboxValue}
              onChange={(e) => setMailboxValue(e.target.value)}
              placeholder="e.g. the Entra group's object ID"
            />
          </label>
        </div>
      )}
      <p className="adm-muted adm-small">
        {current &&
          'To use another provider or another app registration, disconnect this one and connect the new one. '}
        The secret is kept by the sign-in service only and is never shown again. With an
        administrators&apos; claim, whoever carries it is made an administrator each time they sign
        in through the provider, and whoever does not is no longer one. A mailbox made at first
        sign-in has the organisation&apos;s default quota and takes a seat; when no seat is free,
        the person is not let in and the refusal is in the audit log.
      </p>
      <div className="adm-inline">
        <button type="submit" className="btn" disabled={busy}>
          {current ? 'Save changes' : 'Connect'}
        </button>
        {onCancel && (
          <button type="button" className="btn ghost" onClick={onCancel}>
            Cancel
          </button>
        )}
        {error && <span className="adm-error">{error}</span>}
      </div>
    </form>
  );
}

// ─── Directory sync ──────────────────────────────────────────────────────────

export function DirectorySyncPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<DirectorySync>(`${base(slug)}/directory-sync`, { signal }),
    `directory-sync:${slug}`,
  );
  return (
    <section className="adm-panel">
      <div className="adm-panel-head">
        <h3>Leavers from the tenant&apos;s directory</h3>
        {active && (
          <ActionButton
            tone="ghost"
            run={async () => {
              await api(`${base(slug)}/directory-sync`, { method: 'POST' });
              reload();
            }}
          >
            Check now
          </ActionButton>
        )}
      </div>
      <p className="adm-muted">
        Every 15 minutes the tenant&apos;s sign-in directory is checked. A person disabled there, or
        removed after signing in at least once, has their mailbox disabled, their sessions ended and
        their app passwords revoked. Mailboxes disabled this way come back when the person is
        enabled again. People who sign in through Google or another external provider are only
        caught here once an administrator disables them in the tenant&apos;s realm; use SCIM where
        the provider supports it.
      </p>
      <Loaded load={load}>
        {({ synced_at, report }) =>
          !synced_at || !report ? (
            <p className="adm-muted">Not checked yet.</p>
          ) : (
            <>
              <p>
                Checked {when(synced_at)}: {report.users} people in the directory, {report.matched}{' '}
                matched to mailboxes.
                {report.disabled.length > 0 && ` Disabled: ${report.disabled.join(', ')}.`}
                {report.enabled.length > 0 && ` Enabled again: ${report.enabled.join(', ')}.`}
              </p>
              {report.held && (
                <p className="adm-error" role="alert">
                  {report.held}
                </p>
              )}
              {report.error && (
                <p className="adm-error" role="alert">
                  {report.error}
                </p>
              )}
            </>
          )
        }
      </Loaded>
    </section>
  );
}

/**
 * Whether people in this organisation may forward their mail to addresses
 * outside it. On by default; the tenant admin decides. The mail plane enforces
 * it, so a forward written by any mail client is held to it too.
 */
export function ForwardingPolicyPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<{ external_forwarding_allowed: boolean }>(`${base(slug)}/policy`, { signal }),
    `policy:${slug}`,
  );
  return (
    <section className="adm-panel">
      <h3>Forwarding outside the organisation</h3>
      <p className="adm-muted">
        People can always forward their mail to addresses in this organisation&apos;s own domains.
        When this is off, forwarding to any other address is refused by the mail server and the
        message stays in their mailbox — including forwards set up in a desktop mail app.
      </p>
      <Loaded load={load}>
        {({ external_forwarding_allowed: allowed }) => (
          <div className="adm-inline">
            <span>
              {allowed ? (
                <Badge tone="ok">Allowed</Badge>
              ) : (
                <Badge tone="warn">Only inside the organisation</Badge>
              )}
            </span>
            {active && (
              <ActionButton
                tone="ghost"
                run={async () => {
                  await api(`${base(slug)}/policy`, {
                    method: 'POST',
                    body: { external_forwarding_allowed: !allowed },
                  });
                  reload();
                }}
              >
                {allowed ? 'Allow only inside the organisation' : 'Allow forwarding anywhere'}
              </ActionButton>
            )}
          </div>
        )}
      </Loaded>
    </section>
  );
}

/**
 * Whether the desktop app may move people's mail off the server into an
 * archive on their own computer (ADR-0040). On by default. Mail moved there is
 * only on that computer: outside the server's backups, retention and
 * eDiscovery, so an organisation that must keep everything turns it off.
 * Opening Outlook data files and copying from them to the mailbox stay.
 */
export function ArchiveMovePolicyPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<{ local_archive_move_allowed: boolean }>(`${base(slug)}/policy`, { signal }),
    `policy:${slug}:archive-move`,
  );
  return (
    <section className="adm-panel">
      <h3>Moving mail to archives on people&apos;s computers</h3>
      <p className="adm-muted">
        In the desktop app, people can open Outlook data files (.pst) and move mail from their
        mailbox into an archive on their computer to free up space. Mail moved there is only on that
        computer: it is not in webmail or on phones, not in the server&apos;s backups, and not
        covered by retention, legal hold or eDiscovery. Turn this off if all mail must stay on the
        server; opening archives and copying from them back to the mailbox still work.
      </p>
      <Loaded load={load}>
        {({ local_archive_move_allowed: allowed }) => (
          <div className="adm-inline">
            <span>
              {allowed ? (
                <Badge tone="warn">Allowed</Badge>
              ) : (
                <Badge tone="ok">Mail stays on the server</Badge>
              )}
            </span>
            {active && (
              <ActionButton
                tone="ghost"
                run={async () => {
                  await api(`${base(slug)}/policy`, {
                    method: 'POST',
                    body: { local_archive_move_allowed: !allowed },
                  });
                  reload();
                }}
              >
                {allowed ? 'Keep all mail on the server' : 'Allow moving mail to archives'}
              </ActionButton>
            )}
          </div>
        )}
      </Loaded>
    </section>
  );
}

/**
 * Whether people may make app passwords for mail apps that cannot use the
 * organisation's sign-in page (ADR-0038). Off by default (PRD §5.5). The mail
 * server checks it at every sign-in, so turning it off stops existing ones too.
 */
export function AppPasswordsPolicyPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<{ app_passwords_allowed: boolean }>(`${base(slug)}/policy`, { signal }),
    `policy:${slug}:app-passwords`,
  );
  return (
    <section className="adm-panel">
      <h3>App passwords</h3>
      <p className="adm-muted">
        Apple Mail, the iPhone and Android mail apps and scripts cannot use this organisation&apos;s
        sign-in page. With app passwords allowed, people make one for each such app in their webmail
        settings; it skips multi-factor sign-in, so allow it only if they need those apps. Turning
        it off stops every existing app password at its next sign-in.
      </p>
      <Loaded load={load}>
        {({ app_passwords_allowed: allowed }) => (
          <div className="adm-inline">
            <span>
              {allowed ? <Badge tone="warn">Allowed</Badge> : <Badge tone="ok">Not allowed</Badge>}
            </span>
            {active && (
              <ActionButton
                confirm={
                  allowed
                    ? 'Stop app passwords for the whole organisation? Every phone and mail app signed in with one stops at once.'
                    : undefined
                }
                tone="ghost"
                run={async () => {
                  await api(`${base(slug)}/policy`, {
                    method: 'POST',
                    body: { app_passwords_allowed: !allowed },
                  });
                  reload();
                }}
              >
                {allowed ? 'Stop app passwords' : 'Allow app passwords'}
              </ActionButton>
            )}
          </div>
        )}
      </Loaded>
    </section>
  );
}

/**
 * How long the desktop app stays signed in unused (ADR-0052): a week or 30
 * days. While it is used it stays signed in; "Sign out everywhere" on a
 * mailbox ends it at once either way.
 */
export function AppSignInPolicyPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<{ app_sign_in_days: number }>(`${base(slug)}/policy`, { signal }),
    `policy:${slug}:app-sign-in`,
  );
  return (
    <section className="adm-panel">
      <h3>Desktop app sign-in</h3>
      <p className="adm-muted">
        People stay signed in to the desktop mail app while they use it. When it goes unused for
        this long, say on a laptop left switched off, they sign in again. Sign out everywhere, on a
        person&apos;s mailbox, signs them out of it at once.
      </p>
      <Loaded load={load}>
        {({ app_sign_in_days: days }) => (
          <div className="adm-inline">
            <span>
              <Badge tone="neutral">{days} days unused</Badge>
            </span>
            {active && (
              <ActionButton
                tone="ghost"
                run={async () => {
                  await api(`${base(slug)}/policy`, {
                    method: 'POST',
                    body: { app_sign_in_days: days === 7 ? 30 : 7 },
                  });
                  reload();
                }}
              >
                {days === 7 ? 'Change to 30 days' : 'Change to 7 days'}
              </ActionButton>
            )}
          </div>
        )}
      </Loaded>
      {active && <SignOutEveryone slug={slug} />}
    </section>
  );
}

/**
 * Whether everyone must use two-step sign-in (ADR-0058). Off: people turn it
 * on themselves in Settings. On: Keycloak makes anyone without an
 * authenticator set one up at their next sign-in; people already signed in
 * are asked when they next sign in.
 */
export function TwoStepPolicyPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<{ two_step_required: boolean }>(`${base(slug)}/policy`, { signal }),
    `policy:${slug}:two-step`,
  );
  return (
    <section className="adm-panel">
      <h3>Two-step sign-in</h3>
      <p className="adm-muted">
        After their password, people give a code from an authenticator app on their phone. Anyone
        can turn it on in their webmail settings, and save recovery codes for a lost phone. When you
        require it, everyone who has not set it up is made to at their next sign-in — for every
        Genworx service they sign in to with this account, not only mail. People who sign in through
        your own identity provider give its second step instead.
      </p>
      <Loaded load={load}>
        {({ two_step_required: required }) => (
          <div className="adm-inline">
            <span>
              {required ? (
                <Badge tone="ok">Required</Badge>
              ) : (
                <Badge tone="neutral">Optional</Badge>
              )}
            </span>
            {active && (
              <ActionButton
                tone="ghost"
                confirm={
                  required
                    ? 'Make two-step sign-in optional? People who have it keep it; nobody else is asked to set it up.'
                    : 'Require two-step sign-in? Everyone without an authenticator app must set one up at their next sign-in.'
                }
                run={async () => {
                  await api(`${base(slug)}/policy`, {
                    method: 'POST',
                    body: { two_step_required: !required },
                  });
                  reload();
                }}
              >
                {required ? 'Make it optional' : 'Require two-step sign-in'}
              </ActionButton>
            )}
          </div>
        )}
      </Loaded>
    </section>
  );
}

/**
 * Where the organisation's people go when they forget a password their
 * organisation manages: a directory (AD/LDAP) or its own identity provider
 * (ADR-0053). People with a password here reset it themselves instead.
 */
export function PasswordHelpPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<{ url: string | null }>(`${base(slug)}/password-help`, { signal }),
    `policy:${slug}:password-help`,
  );
  return (
    <section className="adm-panel">
      <h3>Password help</h3>
      <p className="adm-muted">
        People whose password belongs to ZitiMail reset it themselves, through the recovery email
        they set in Settings. People who sign in with your directory or your own identity provider
        are shown this page instead, to reset it there.
      </p>
      <Loaded load={load}>
        {({ url }) => (
          <PasswordHelpEditor slug={slug} current={url} active={active} onSaved={reload} />
        )}
      </Loaded>
    </section>
  );
}

function PasswordHelpEditor({
  slug,
  current,
  active,
  onSaved,
}: {
  slug: string;
  current: string | null;
  active: boolean;
  onSaved: () => void;
}) {
  const { base } = useContext(AdminWhereContext);
  const [url, setUrl] = useState(current ?? '');
  const save = (value: string | null) =>
    api(`${base(slug)}/password-help`, { method: 'POST', body: { url: value } }).then(onSaved);
  return (
    <div className="adm-inline">
      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://passwordreset.example.com"
        aria-label="Password help page"
        maxLength={2000}
        disabled={!active}
      />
      {active && (
        <ActionButton run={() => save(url.trim() || null)}>
          {url.trim() ? 'Save' : 'Save (none: “ask your IT team”)'}
        </ActionButton>
      )}
      {active && current && (
        <ActionButton
          tone="ghost"
          run={async () => {
            setUrl('');
            await save(null);
          }}
        >
          Remove
        </ActionButton>
      )}
    </div>
  );
}

const SIGN_OUT_EVERYONE =
  'Sign everyone in the organisation out of ZitiMail and ZitiMeet? Each person will need ' +
  'their password, and their authenticator code if they use one, to sign in again.';

/** Everyone out at once, for a security incident (ADR-0052). */
function SignOutEveryone({ slug }: { slug: string }) {
  const { base } = useContext(AdminWhereContext);
  const [done, setDone] = useState<number | null>(null);
  return (
    <div className="adm-inline">
      <ActionButton
        tone="danger"
        run={async () => {
          if (!window.confirm(SIGN_OUT_EVERYONE)) {
            return;
          }
          const out = await api<{ mailboxes: number }>(`${base(slug)}/sign-out-everyone`, {
            method: 'POST',
          });
          setDone(out.mailboxes);
        }}
      >
        Sign everyone out
      </ActionButton>
      <p className="adm-muted adm-small" role="status">
        {done === null
          ? 'For a security incident: ends every sign-in to ZitiMail (desktop app, phones, ' +
            'mail programs and webmail) and to ZitiMeet. App passwords keep working; ' +
            'products outside ZitiOffice are not affected.'
          : `Signed out: ${done} ${done === 1 ? 'mailbox' : 'mailboxes'}.`}
      </p>
    </div>
  );
}

// ─── The organisation's disclaimer (ADR-0046) ────────────────────────────────

interface Disclaimer {
  readonly text: string | null;
  readonly external_only: boolean;
}

export function DisclaimerPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<Disclaimer>(`${base(slug)}/disclaimer`, { signal }),
    `disclaimer:${slug}`,
  );
  return (
    <section className="adm-panel">
      <h3>Disclaimer</h3>
      <p className="adm-muted">
        Added below the text of the mail your people send, from webmail and from every mail app. It
        is signed with the message, so it cannot be removed on the way.
      </p>
      <Loaded load={load}>
        {(d) => (
          <DisclaimerForm
            key={`${d.text ?? ''}|${d.external_only}`}
            slug={slug}
            current={d}
            active={active}
            onSaved={reload}
          />
        )}
      </Loaded>
    </section>
  );
}

function DisclaimerForm({
  slug,
  current,
  active,
  onSaved,
}: {
  slug: string;
  current: Disclaimer;
  active: boolean;
  onSaved: () => void;
}) {
  const { base } = useContext(AdminWhereContext);
  const [text, setText] = useState(current.text ?? '');
  const [externalOnly, setExternalOnly] = useState(current.external_only);
  const save = (value: string) =>
    api(`${base(slug)}/disclaimer`, {
      method: 'POST',
      body: { text: value, external_only: externalOnly },
    }).then(onSaved);
  return (
    <>
      <textarea
        className="adm-textarea"
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={2000}
        disabled={!active}
        placeholder="This email and any attachments are confidential and intended only for the addressee…"
        aria-label="Disclaimer text"
      />
      <div className="adm-inline">
        <label className="adm-check">
          <input
            type="checkbox"
            checked={externalOnly}
            onChange={(e) => setExternalOnly(e.target.checked)}
            disabled={!active}
          />{' '}
          Only on mail to people outside the organisation
        </label>
        {active && (
          <ActionButton run={() => save(text)}>
            {text.trim() ? 'Save disclaimer' : 'Save (no disclaimer)'}
          </ActionButton>
        )}
        {active && current.text && (
          <ActionButton tone="ghost" run={() => save('')}>
            Remove
          </ActionButton>
        )}
      </div>
    </>
  );
}
